/**
 * PdfViewer — renders every page of a PDF stacked vertically on individual
 * <canvas> elements via pdfjs-dist. Users scroll the modal up/down to move
 * through the document; no prev/next buttons.
 *
 * Why not an <iframe>:
 *   Some browsers (Arc with Shields on, mobile Safari, anything with strict
 *   privacy extensions) block embedded PDFs. PDF.js parses the bytes
 *   client-side and paints them on a canvas, which no shield can block.
 *
 * The pdfjs library + worker are dynamically imported so the ~300 KB chunk
 * only loads when the user actually opens a preview.
 *
 * Props:
 *   url — PDF source (presigned/proxy URL); should be CORS-fetchable.
 */
import { useEffect, useRef, useState } from 'react'
import { Loader2, AlertTriangle } from 'lucide-react'

let pdfjsModulePromise = null
function getPdfjs() {
  if (!pdfjsModulePromise) {
    pdfjsModulePromise = (async () => {
      const [pdfjs, WorkerCtor] = await Promise.all([
        import('pdfjs-dist/build/pdf.mjs'),
        import('pdfjs-dist/build/pdf.worker.mjs?worker'),
      ])
      pdfjs.GlobalWorkerOptions.workerPort = new WorkerCtor.default()
      return pdfjs
    })()
  }
  return pdfjsModulePromise
}

export default function PdfViewer({ url }) {
  const [doc, setDoc] = useState(null)
  const [busy, setBusy] = useState(true)
  const [err, setErr]   = useState(null)

  useEffect(() => {
    let cancelled = false
    setBusy(true); setErr(null); setDoc(null)
    ;(async () => {
      try {
        const pdfjs = await getPdfjs()
        const d = await pdfjs.getDocument(url).promise
        if (cancelled) { d.destroy(); return }
        setDoc(d)
      } catch (e) {
        if (!cancelled) setErr(e?.message || String(e))
      } finally {
        if (!cancelled) setBusy(false)
      }
    })()
    return () => { cancelled = true }
  }, [url])

  if (err) {
    return (
      <div className="w-full h-full flex items-center justify-center p-6">
        <div className="bg-white rounded-lg p-5 max-w-[420px] text-center text-rose-700 space-y-2">
          <AlertTriangle size={24} className="mx-auto" />
          <div className="text-[13px] font-medium">โหลด PDF ไม่สำเร็จ</div>
          <div className="text-[11.5px] text-zinc-500">{err}</div>
        </div>
      </div>
    )
  }
  if (busy && !doc) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/80">
        <Loader2 className="animate-spin" />
        <span className="text-[12px]">กำลังโหลดเอกสาร…</span>
      </div>
    )
  }

  const total = doc?.numPages || 0
  return (
    <div onClick={(e) => e.stopPropagation()}
      className="w-full h-full max-w-[1100px] flex flex-col bg-zinc-100 rounded-md shadow-2xl overflow-hidden">
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col items-center gap-3 py-4 px-4">
          {Array.from({ length: total }, (_, i) => (
            <PdfPage key={i} doc={doc} pageNumber={i + 1} />
          ))}
        </div>
      </div>
      {total > 1 && (
        <div className="px-3 py-1.5 bg-white border-t border-zinc-100 text-center text-[11.5px] text-zinc-500 tabular-nums">
          {total} หน้า · เลื่อนลงเพื่อดูหน้าถัดไป
        </div>
      )}
    </div>
  )
}

// Renders a single PDF page onto its own canvas. Sized to the parent's CSS
// width (capped at 1100px) × devicePixelRatio for crisp output.
function PdfPage({ doc, pageNumber }) {
  const canvasRef = useRef(null)
  const renderTaskRef = useRef(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!doc || !canvasRef.current) return
    let cancelled = false
    if (renderTaskRef.current) {
      try { renderTaskRef.current.cancel() } catch {}
      renderTaskRef.current = null
    }
    ;(async () => {
      try {
        const pageObj = await doc.getPage(pageNumber)
        if (cancelled) return
        const canvas = canvasRef.current
        const container = canvas.parentElement
        const cssWidth = Math.min(container?.clientWidth || 800, 1100)
        const scale = cssWidth / pageObj.getViewport({ scale: 1 }).width
        const dpr = window.devicePixelRatio || 1
        const viewport = pageObj.getViewport({ scale: scale * dpr })
        canvas.width  = Math.round(viewport.width)
        canvas.height = Math.round(viewport.height)
        canvas.style.width  = `${Math.round(viewport.width  / dpr)}px`
        canvas.style.height = `${Math.round(viewport.height / dpr)}px`
        const ctx = canvas.getContext('2d')
        const task = pageObj.render({ canvasContext: ctx, viewport, canvas })
        renderTaskRef.current = task
        await task.promise
      } catch (e) {
        if (!cancelled && e?.name !== 'RenderingCancelledException') {
          setErr(e?.message || String(e))
        }
      }
    })()
    return () => {
      cancelled = true
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel() } catch {}
        renderTaskRef.current = null
      }
    }
  }, [doc, pageNumber])

  if (err) {
    return (
      <div className="bg-white shadow-md w-full max-w-full px-4 py-3 text-[11.5px] text-rose-700">
        หน้า {pageNumber} โหลดไม่สำเร็จ: {err}
      </div>
    )
  }
  return <canvas ref={canvasRef} className="bg-white shadow-md max-w-full" />
}
